import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, Card, CardHeader, DatePicker, ErrorState, Icon, IconButton, MenuDropdown, PageHeader, PageLoader, useToast } from '../../../components/ui';
import { useCrumb } from '../../../components/layout/Breadcrumbs/Breadcrumbs';
import { siteText } from '../../../components/common/DocPreview/DocPreview';
import { PartyCard } from '../../../components/common/PartyCard/PartyCard';
import { WorkerMultiSelect } from '../../../components/common/Pickers/Pickers';
import { HistoryCard, KeyValues, StatusBadge, TwoColumn } from '../../../components/common/Parts/Parts';
import { useAuth } from '../../../app/providers/AuthProvider';
import { useAction } from '../../../hooks/useAction';
import { useFetch } from '../../../hooks/useFetch';
import { JOB_STATUS, PRIORITY } from '../../../constants/status';
import { labelOf } from '../../../utils/labels';
import { masterLabel } from '../../masters/services/masterService';
import { useMasters } from '../../masters/hooks/useMasters';
import { formatDate, toISODate } from '../../../utils/date';
import { money } from '../../../utils/currency';
import { loadSettings } from '../../settings/services/settingsService';
import { ACTION_LABEL, TRANSITIONS, WORKER_ALLOWED } from '../constants/jobFlow';
import { JobMaterials } from '../components/JobMaterials/JobMaterials';
import { NotesCard, PhotosCard } from '../components/JobSiteCards/JobSiteCards';
import { jobService } from '../services/jobService';
import type { JobStatus } from '../types/jobTypes';
import type { ReactNode } from 'react';
import type { ActionProps } from '../../../hooks/useAction';
import type { Job } from '../types/jobTypes';

function AssignCard({ job, run, busy, onChange }: ActionProps & { job: Job; onChange: () => void }) {
  const [workers, setWorkers] = useState<string[]>([]);
  const [date, setDate] = useState('');
  useEffect(() => {
    setWorkers((job.assignedTo ?? []).map((u) => u._id ?? u));
    setDate(job.scheduledDate ? toISODate(new Date(job.scheduledDate)) : '');
  }, [job]);
  const save = async () => {
    if (await run('assign', () => jobService.assign(job._id, workers, date || undefined), 'Workers assigned')) onChange();
  };
  return (
    <Card>
      <CardHeader title="Assign workers" />
      <div className="space-y-3 p-5 pt-4">
        <WorkerMultiSelect value={workers} onChange={setWorkers} />
        <DatePicker label="Visit date" value={date} onChange={setDate} />
        <Button fullWidth leftIcon="users" loading={busy === 'assign'} disabled={!workers.length} onClick={save}>Save assignment</Button>
      </div>
    </Card>
  );
}

export default function JobDetails() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { isStaff } = useAuth();
  const { busy, run } = useAction(toast);
  const [gstin, setGstin] = useState<string | null>(null);
  useMasters();
  const { data: job, error, reload } = useFetch(() => jobService.get(id), [id]);
  useCrumb(`/jobs/${id}`, job?.serviceNo);
  useEffect(() => {
    if (isStaff) loadSettings().then((s) => setGstin(s.gstin || '')).catch(() => {});
  }, [isStaff]);

  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!job) return <PageLoader />;

  const editable = !['invoiced', 'cancelled'].includes(job.status);
  const allowed = (TRANSITIONS[job.status] ?? []).filter((s) => isStaff || WORKER_ALLOWED.includes(s));
  const setStatus = async (next: JobStatus) => {
    if (await run(next, () => jobService.setStatus(id, next), `Job ${labelOf(JOB_STATUS, next).toLowerCase()}`)) reload();
  };
  const createInvoice = async (invoiceType) => {
    const inv = await run('invoice', () => jobService.createInvoice(id, invoiceType), 'Draft invoice created');
    if (inv && inv !== true) navigate(`/invoices/${inv._id}`);
  };

  // One main button for the natural next step; the rest go in the menu.
  const nextStep: JobStatus | null = (['in_progress', 'completed'] as JobStatus[]).find((s) => allowed.includes(s)) ?? (job.status === 'cancelled' && isStaff ? 'pending' : null);
  let primary: ReactNode = null;
  let usedStep: JobStatus | null = null;
  if (job.status === 'completed' && isStaff && !job.invoiceId) {
    primary = <Button leftIcon="receipt" loading={busy === 'invoice'} onClick={() => createInvoice(gstin ? 'gst' : 'non_gst')}>Create invoice</Button>;
  } else if (job.invoiceId && isStaff) {
    primary = <Button leftIcon="receipt" onClick={() => navigate(`/invoices/${job.invoiceId}`)}>Open invoice</Button>;
  } else if (nextStep) {
    usedStep = nextStep;
    const [label, icon] = job.status === 'on_hold' && nextStep === 'in_progress' ? ['Resume work', 'wrench'] : ACTION_LABEL[nextStep] ?? [labelOf(JOB_STATUS, nextStep), 'arrowRight'];
    primary = <Button leftIcon={icon} loading={busy === nextStep} onClick={() => setStatus(nextStep)}>{job.status === 'completed' ? 'Reopen' : label}</Button>;
  }
  const menu = [
    ...allowed.filter((s) => s !== usedStep && s !== 'assigned').map((s) => ({ label: s === 'in_progress' ? 'Reopen (in progress)' : ACTION_LABEL[s]?.[0] ?? labelOf(JOB_STATUS, s), icon: ACTION_LABEL[s]?.[1] ?? 'arrowRight', danger: s === 'cancelled', onClick: () => setStatus(s) })),
    isStaff && job.status === 'completed' && !job.invoiceId && { label: gstin ? 'Create non-GST invoice' : 'Create GST invoice', icon: 'receipt', onClick: () => createInvoice(gstin ? 'non_gst' : 'gst') },
    isStaff && editable && { label: 'Buy material for this job', icon: 'cart', onClick: () => navigate(`/purchases/new?serviceId=${id}`) },
    isStaff && { label: 'Add expense for this job', icon: 'moneyOut', onClick: () => navigate(`/expenses/new?serviceId=${id}`) },
  ].filter(Boolean);

  const labourTotal = job.labourItems.reduce((a, l) => a + Math.round(l.qty * (l.rate ?? 0)) - (l.discount ?? 0), 0);

  return (
    <>
      <PageHeader
        title={job.serviceNo}
        badge={
          <span className="flex gap-2">
            <StatusBadge map={JOB_STATUS} value={job.status} />
            {job.priority && job.priority !== 'normal' && <StatusBadge map={PRIORITY} value={job.priority} />}
          </span>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-x-5 gap-y-1">
            <span className="inline-flex items-center gap-1.5"><Icon name="user" size={15} />{job.customer?.name}</span>
            {job.title && <span className="inline-flex items-center gap-1.5"><Icon name="wrench" size={15} />{job.title}</span>}
            <span className="inline-flex items-center gap-1.5"><Icon name="calendar" size={15} />{job.scheduledDate ? formatDate(job.scheduledDate) : 'No visit date'}</span>
          </span>
        }
        actions={
          <>
            {isStaff && editable && <Button variant="outline" leftIcon="edit" onClick={() => navigate(`/jobs/${id}/edit`)}>Edit</Button>}
            {menu.length > 0 && <MenuDropdown trigger={<IconButton icon="more" label="More actions" variant="outline" />} items={menu} />}
            {primary}
          </>
        }
      />

      <TwoColumn
        main={
          <>
            <Card className="p-5">
              <dl className="grid gap-5 sm:grid-cols-3">
                <div>
                  <dt className="text-[13px] text-ink-400">Type of work</dt>
                  <dd className="text-sm font-medium">{masterLabel('serviceType', job.serviceType)}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-[13px] text-ink-400">Site</dt>
                  <dd className="text-sm font-medium">{siteText(job.siteAddress) || '-'}</dd>
                </div>
                {job.quotationId && isStaff && (
                  <div>
                    <dt className="text-[13px] text-ink-400">From quotation</dt>
                    <dd><Link to={`/quotations/${job.quotationId}`} className="text-sm font-medium text-brand-600 hover:underline">Open quotation</Link></dd>
                  </div>
                )}
                {job.description && (
                  <div className="sm:col-span-3">
                    <dt className="text-[13px] text-ink-400">Work details</dt>
                    <dd className="whitespace-pre-line text-sm">{job.description}</dd>
                  </div>
                )}
              </dl>
            </Card>

            <Card className="overflow-hidden">
              <CardHeader title="Labour" subtitle={isStaff && editable ? 'Change labour from Edit' : undefined} className="pb-4" />
              {job.labourItems.length === 0 ? (
                <p className="border-t border-line px-5 py-8 text-center text-sm text-ink-400">No labour lines.</p>
              ) : (
                <table className="w-full text-left text-sm">
                  <tbody className="tabular divide-y divide-line border-t border-line">
                    {job.labourItems.map((l) => (
                      <tr key={l._id}>
                        <td className="px-5 py-3 font-medium text-navy-900">{l.description}</td>
                        <td className="whitespace-nowrap px-5 py-3 text-right">{l.qty} {l.unit}</td>
                        {isStaff && <td className="px-5 py-3 text-right">{money(l.rate)}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>

            <JobMaterials job={job} isStaff={isStaff} editable={editable} run={run} busy={busy} onChange={reload} />
            <PhotosCard job={job} editable={editable} run={run} busy={busy} onChange={reload} />
            <NotesCard job={job} run={run} busy={busy} onChange={reload} />
          </>
        }
        aside={
          <>
            <Card>
              <CardHeader title="Summary" />
              <div className="p-5 pt-4">
                <KeyValues
                  rows={[
                    ['Status', <StatusBadge map={JOB_STATUS} value={job.status} />],
                    ['Visit date', job.scheduledDate ? formatDate(job.scheduledDate) : 'Not set'],
                    ['Workers', job.assignedTo?.length ? job.assignedTo.map((u) => u.name).join(', ') : 'Not assigned'],
                    job.startedAt && ['Started', formatDate(job.startedAt)],
                    job.completedAt && ['Work finished on', formatDate(job.completedAt)],
                    isStaff && ['Labour (before GST)', money(labourTotal)],
                  ]}
                />
                {job.invoiceId && isStaff && (
                  <Badge tone="blue" className="mt-4">Invoice created</Badge>
                )}
              </div>
            </Card>
            {isStaff && editable && <AssignCard job={job} run={run} busy={busy} onChange={reload} />}
            <PartyCard party={job.customer} to={isStaff ? `/customers/${job.customerId}` : undefined} />
            <HistoryCard history={job.statusHistory} statusMap={JOB_STATUS} />
          </>
        }
      />
    </>
  );
}
